import {
  CUSTOMER_AUTH_CHANGED_EVENT,
  CUSTOMER_ORDER_NOTIFY_EVENT,
  connectAppNotify,
  type AppNotifyMessage,
} from "@/lib/app-notify";
import { auth } from "@/lib/auth";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const NOTICE_DURATION_MS = 4500;

interface CustomerNoticeView {
  title: string;
  body: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  color: string;
  label: string;
}

const NOTICE_VIEW_MAP: Record<string, CustomerNoticeView> = {
  CUSTOMER_ORDER_ACCEPTED: {
    title: "技师已接单",
    body: "技师已接受您的订单，正在准备出发，请保持手机畅通。",
    icon: "ri-checkbox-circle-fill",
    iconBg: "linear-gradient(135deg, #FF8C3A, #FF5C00)",
    iconColor: "#fff",
    color: "#FF5C00",
    label: "接单通知",
  },
  CUSTOMER_COACH_DEPARTED: {
    title: "技师正在出发",
    body: "技师已从出发地启程，请留意服务进度。",
    icon: "ri-motorbike-fill",
    iconBg: "linear-gradient(135deg, #FFC840, #F0A500)",
    iconColor: "#fff",
    color: "#F0A500",
    label: "出发通知",
  },
  CUSTOMER_COACH_ARRIVED: {
    title: "技师已到达",
    body: "技师已到达您的预约地点，请做好接待准备。",
    icon: "ri-map-pin-2-fill",
    iconBg: "linear-gradient(135deg, #4FBFFF, #1890FF)",
    iconColor: "#fff",
    color: "#1890FF",
    label: "到达通知",
  },
  CUSTOMER_SERVICE_STARTED: {
    title: "服务已开始",
    body: "您的服务已正式开始，祝您体验愉快。",
    icon: "ri-service-fill",
    iconBg: "linear-gradient(135deg, #3CD898, #17B978)",
    iconColor: "#fff",
    color: "#17B978",
    label: "服务通知",
  },
  CUSTOMER_SERVICE_COMPLETED: {
    title: "服务已完成",
    body: "本次服务已结束，欢迎留下您的真实评价。",
    icon: "ri-medal-fill",
    iconBg: "linear-gradient(135deg, #3CD898, #17B978)",
    iconColor: "#fff",
    color: "#17B978",
    label: "完成通知",
  },
  CUSTOMER_APPEND_PAID: {
    title: "加钟成功",
    body: "您的加钟申请已成功支付，服务时长已延长。",
    icon: "ri-time-fill",
    iconBg: "linear-gradient(135deg, #FF8C3A, #FF5C00)",
    iconColor: "#fff",
    color: "#FF7B22",
    label: "加钟通知",
  },
  CUSTOMER_APPEND_REQUESTED: {
    title: "技师已帮你加钟",
    body: "技师已为当前订单发起加钟，请进入订单查看并完成确认。",
    icon: "ri-time-fill",
    iconBg: "linear-gradient(135deg, #FF8C3A, #FF5C00)",
    iconColor: "#fff",
    color: "#FF7B22",
    label: "加钟提醒",
  },
  CUSTOMER_ORDER_CANCELLED: {
    title: "订单已取消",
    body: "订单已取消，如涉及退款请留意到账进度。",
    icon: "ri-close-circle-fill",
    iconBg: "linear-gradient(135deg, #FF7070, #FF3B3B)",
    iconColor: "#fff",
    color: "#FF4D4D",
    label: "取消通知",
  },
};

const getNoticeView = (notice: AppNotifyMessage): CustomerNoticeView | null =>
  NOTICE_VIEW_MAP[notice.eventType || ""] || null;

const isCustomerOrderEvent = (message: AppNotifyMessage) =>
  Boolean(message.eventType?.startsWith("CUSTOMER_") && message.orderId);

export default function CustomerNotifyProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [notice, setNotice] = useState<AppNotifyMessage | null>(null);
  const connectionRef = useRef<ReturnType<typeof connectAppNotify> | null>(null);
  const tokenRef = useRef("");
  const noticeTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const connect = () => {
      const token = auth.getToken();
      if (!token) {
        connectionRef.current?.close();
        connectionRef.current = null;
        tokenRef.current = "";
        return;
      }
      if (token === tokenRef.current && connectionRef.current) {
        return;
      }
      connectionRef.current?.close();
      tokenRef.current = token;
      connectionRef.current = connectAppNotify("customer", token, (message) => {
        if (message.eventType && message.eventType !== "PONG") {
          if (isCustomerOrderEvent(message)) {
            window.dispatchEvent(new CustomEvent(CUSTOMER_ORDER_NOTIFY_EVENT, { detail: message }));
          }
          if (getNoticeView(message)) {
            if (noticeTimerRef.current) {
              window.clearTimeout(noticeTimerRef.current);
            }
            setNotice(message);
            noticeTimerRef.current = window.setTimeout(() => {
              setNotice(null);
              noticeTimerRef.current = null;
            }, NOTICE_DURATION_MS);
          }
        }
      });
    };

    connect();
    window.addEventListener(CUSTOMER_AUTH_CHANGED_EVENT, connect);
    window.addEventListener("focus", connect);
    return () => {
      window.removeEventListener(CUSTOMER_AUTH_CHANGED_EVENT, connect);
      window.removeEventListener("focus", connect);
      connectionRef.current?.close();
      connectionRef.current = null;
      tokenRef.current = "";
      if (noticeTimerRef.current) {
        window.clearTimeout(noticeTimerRef.current);
        noticeTimerRef.current = null;
      }
    };
  }, []);

  const closeNotice = () => {
    if (noticeTimerRef.current) {
      window.clearTimeout(noticeTimerRef.current);
      noticeTimerRef.current = null;
    }
    setNotice(null);
  };

  const viewOrder = () => {
    closeNotice();
    navigate("/orders");
  };

  const orderIdentity = notice?.orderSn || (notice?.orderId ? `#${notice.orderId}` : "");
  const noticeView = notice ? getNoticeView(notice) : null;
  const timeText = notice?.eventTime
    ? new Date(notice.eventTime).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <>
      {children}
      {notice && noticeView && (
        <div
          className="customer-fixed-shell pointer-events-none fixed inset-x-0 top-0 z-[1000] flex flex-col items-center gap-2 px-4 pt-4"
        >
          <div
            className="customer-notify-toast pointer-events-auto relative overflow-hidden bg-white"
            style={{
              width: "calc(100vw - 32px)",
              maxWidth: 420,
              border: `1px solid ${noticeView.color}22`,
              borderRadius: 20,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <div
              className="absolute bottom-0 left-0 top-0 w-1"
              style={{ background: noticeView.color, borderRadius: "20px 0 0 20px" }}
            />

            <div className="px-3.5 pb-2.5 pt-3.5" style={{ paddingLeft: 18 }}>
              <div className="flex items-start gap-3">
                <div
                  className="relative flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[14px]"
                  style={{ background: noticeView.iconBg }}
                >
                  <i className={noticeView.icon} style={{ fontSize: 22, color: noticeView.iconColor }} />
                  <div
                    className="customer-notify-ring absolute pointer-events-none"
                    style={{ inset: -4, borderRadius: 18, border: `2px solid ${noticeView.color}` }}
                  />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="mb-0.5 flex min-w-0 items-center gap-1.5">
                    <span
                      className="rounded-full px-2 py-px text-[11px] font-bold"
                      style={{ background: `${noticeView.color}18`, color: noticeView.color }}
                    >
                      {noticeView.label}
                    </span>
                    {timeText ? (
                      <span className="ml-auto flex-shrink-0 text-[11px]" style={{ color: "#BBBBBB" }}>
                        {timeText}
                      </span>
                    ) : null}
                  </div>

                  <p className="my-0.5 text-sm font-bold leading-snug" style={{ color: "#1A1A1A" }}>
                    {noticeView.title}
                  </p>

                  <p className="m-0 line-clamp-2 text-xs leading-relaxed" style={{ color: "#888888" }}>
                    {noticeView.body}
                  </p>

                  <div className="mt-2 flex min-w-0 items-center gap-1.5">
                    <span
                      className="h-[22px] w-[22px] flex-shrink-0 rounded-full"
                      style={{ border: `1.5px solid ${noticeView.color}50` }}
                    />
                    <span className="min-w-0 flex-1 truncate text-xs font-semibold" style={{ color: noticeView.color }}>
                      {orderIdentity || "订单状态更新"}
                    </span>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        viewOrder();
                      }}
                      className="flex flex-shrink-0 cursor-pointer items-center gap-1 rounded-full border-0 px-2.5 py-[3px] text-xs font-bold"
                      style={{ color: noticeView.color, background: `${noticeView.color}14` }}
                    >
                      查看详情
                      <i className="ri-arrow-right-s-line text-[13px]" />
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={closeNotice}
                  className="mt-[-2px] mr-[-4px] flex h-6 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full border-0"
                  style={{ background: "#F5F5F5" }}
                  aria-label="关闭通知"
                >
                  <i className="ri-close-line text-sm" style={{ color: "#AAAAAA" }} />
                </button>
              </div>
            </div>

            <div className="h-[3px]" style={{ background: "#F5F5F5" }}>
              <div className="customer-notify-progress h-full" style={{ background: noticeView.color }} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
